/**
 * Oplog — émetteur d'événements d'activité (couche dessin).
 *
 * Pont DÉCOUPLÉ entre les commandes (qui savent quelles entités changent) et la
 * persistance (couche app / IndexedDB). La couche dessin émet des events identifiés
 * par `entityUid` (résolu depuis `DR_META`) ; l'app s'abonne (`onAdd`/`onRemove`) et
 * persiste avec le `drawingId`. Les entités sans `DR_META` (non-DR) sont ignorées.
 *
 * Undo/redo : une commande mémorise ses events émis (retour de `emitForHandles` /
 * `emitModified`), appelle `revert` à l'undo (retire) et `reemit` au redo (ré-ajoute).
 * Ainsi une action annulée ne laisse aucun event.
 */

import { v4 as uuid } from "uuid";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import { findEntityByHandle, openAs } from "../core/oda";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { readEntityMeta, writeEntityMeta, type EntityMeta } from "./xdata";
import { getEntityType } from "../entities/detection";
import { readKindAndText } from "../parsing/activity";

export type OplogEventType = "CREATED" | "MODIFIED" | "DELETED";

/** Snapshot autonome (DELETED) OU contexte du changement (MODIFIED). */
export interface OplogSnapshot {
  defectName?: string;
  /** Nature de l'entité (ActivityKind) — surtout utile pour le fond de plan sans defectName. */
  kind?: string;
  /** Nature CAD réelle (EntityType) — sert au choix d'icône sur une ligne "supprimé". */
  entityType?: string;
  text?: string;
  /** MODIFIED uniquement : catégorie du changement (text/height/photoNumber/moved/...). */
  changeKind?: string;
  /** MODIFIED uniquement : valeur brute avant/après, si pertinent d'afficher un diff. */
  oldValue?: string;
  newValue?: string;
}

export interface OplogEmit {
  eventId: string;
  entityUid: string;
  type: OplogEventType;
  clientTs: number;
  snapshot?: OplogSnapshot;
}

/** Une entrée à modifier : handle de l'entité + contexte du changement. */
export interface ModifiedEntry {
  handle: string;
  changeKind: string;
  oldValue?: string;
  newValue?: string;
}

type AddHandler = (e: OplogEmit) => void;
type RemoveHandler = (eventId: string) => void;

class OplogEmitter {
  private addHandlers = new Set<AddHandler>();
  private removeHandlers = new Set<RemoveHandler>();

  onAdd(h: AddHandler): () => void {
    this.addHandlers.add(h);
    return () => {
      this.addHandlers.delete(h);
    };
  }

  onRemove(h: RemoveHandler): () => void {
    this.removeHandlers.add(h);
    return () => {
      this.removeHandlers.delete(h);
    };
  }

  /**
   * Résout le DR_META d'une entité, ou stampe une identité minimale à la volée si
   * elle n'en a pas (fond de plan importé, jamais dessiné par DR — role Background).
   * Best-effort : `null` si l'entité est illisible.
   */
  private resolveMeta(
    module: DrawingModuleType,
    id: DrawingModule.OdDbObjectId,
  ): EntityMeta | null {
    try {
      let meta = readEntityMeta(module, id);
      if (!meta) {
        meta = {
          entityUid: uuid(),
          kind: getEntityType(module, id),
          markerNumber: null,
          createdAt: null,
          defectName: null,
          background: true,
          observationId: null,
        };
        writeEntityMeta(module, id, meta);
      }
      return meta;
    } catch {
      return null;
    }
  }

  /**
   * Émet un event par entité des `handles`. Une entité SANS DR_META (fond de plan
   * importé, jamais dessinée par DR) reçoit une identité minimale stampée à la volée
   * — sinon sa suppression serait invisible du fil : seul le hook de création
   * (`stampCreationMeta`) tague les entités, le fond de plan n'y passe jamais.
   * Pour DELETED, joint un snapshot self-contained (defectName/kind/entityType/text)
   * lu sur la même passe — l'entité disparaît, rien d'autre ne pourra la décrire après
   * coup. Pour CREATED, pas de snapshot (l'entité vivante se décrit elle-même).
   * @returns les events émis, à mémoriser par la commande pour undo/redo.
   */
  emitForHandles(
    viewer: DRDrawingViewer,
    type: "CREATED" | "DELETED",
    handles: string[],
  ): OplogEmit[] {
    const module = viewer.module;
    const db = viewer.database;
    if (!db) return [];

    const emitted: OplogEmit[] = [];
    for (const h of handles) {
      const id = findEntityByHandle(module, db, h);
      if (!id) continue;
      const meta = this.resolveMeta(module, id);
      let snapshot: OplogSnapshot | undefined;

      if (meta && type === "DELETED") {
        snapshot = { defectName: meta.defectName ?? undefined };
        // Note : à cet instant l'entité est déjà sur UNDO_LAYER (soft-delete fait
        // par la commande AVANT l'appel) — inutile/faux d'y lire son calque.
        const ent = openAs(id, module.OdDbEntity, OpenMode.kForRead);
        if (ent) {
          try {
            const { kind, entityType, text } = readKindAndText(module, id, ent);
            snapshot.kind = kind;
            snapshot.entityType = entityType;
            snapshot.text = text;
          } finally {
            deleteObj(ent);
          }
        }
      }
      deleteObj(id);
      if (!meta) continue;

      const e: OplogEmit = {
        eventId: uuid(),
        entityUid: meta.entityUid,
        type,
        clientTs: Date.now(),
        snapshot,
      };
      emitted.push(e);
      this.notifyAdd(e);
    }
    return emitted;
  }

  /**
   * Émet un event MODIFIED par entrée, avec son propre contexte de changement
   * (`changeKind`/`oldValue`/`newValue` — cf. `ModifiedEntry`). Même résolution
   * d'identité que `emitForHandles` (stamp à la volée si fond de plan touché).
   * @returns les events émis, à mémoriser par la commande pour undo/redo.
   */
  emitModified(viewer: DRDrawingViewer, entries: ModifiedEntry[]): OplogEmit[] {
    const module = viewer.module;
    const db = viewer.database;
    if (!db) return [];

    const emitted: OplogEmit[] = [];
    for (const entry of entries) {
      const id = findEntityByHandle(module, db, entry.handle);
      if (!id) continue;
      const meta = this.resolveMeta(module, id);
      deleteObj(id);
      if (!meta) continue;

      const e: OplogEmit = {
        eventId: uuid(),
        entityUid: meta.entityUid,
        type: "MODIFIED",
        clientTs: Date.now(),
        snapshot: {
          changeKind: entry.changeKind,
          oldValue: entry.oldValue,
          newValue: entry.newValue,
        },
      };
      emitted.push(e);
      this.notifyAdd(e);
    }
    return emitted;
  }

  /** Undo : retire les events émis. */
  revert(events: OplogEmit[]): void {
    for (const e of events) this.notifyRemove(e.eventId);
  }

  /** Redo : ré-ajoute les events (mêmes eventId). */
  reemit(events: OplogEmit[]): void {
    for (const e of events) this.notifyAdd(e);
  }

  private notifyAdd(e: OplogEmit): void {
    for (const h of Array.from(this.addHandlers)) h(e);
  }

  private notifyRemove(eventId: string): void {
    for (const h of Array.from(this.removeHandlers)) h(eventId);
  }
}

export const oplogEmitter = new OplogEmitter();
